import type { StationeryDraftFormat, StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import type { DigitalProductType, NavigationStep } from './CardCustomizer.types';

const CATEGORY = 'messaging.card';
const PAGE_PREFIX = 'admin';
const PAGE = 'customize';

export interface CustomizePageArgs {
  category: StationeryTemplateCategoryEnum;
  themeId: string;
  step: 'cardFront' | 'cardBack' | 'envelope' | 'recipients' | 'postcardRecipients' | 'review' | 'postCheckout';
}

export interface CustomizationButtonInteractedArgs {
  property: string;
  value?: string | number | boolean;
}

/**
 * Every Segment event name emitted through the generic `track` action.
 *
 * These strings are a live dashboard contract — renaming one silently breaks existing queries,
 * so coordinate with the owning PM before changing an entry. Add a new member here when adding
 * a one-off event; use `findReferences` on the member to locate its emitters.
 */
export type CardCustomizerTrackedAction =
  | 'CardLayerFocused'
  // Music picker audition. Counts previews, not couples; popularity comes from
  // `StationeryOrderCompleted.musicId`, which the shared `stationeryDraftId` joins to.
  | 'DigitalCardMusicPreviewed'
  // Backdrop gallery image click, premium or not. Counts interest, not couples; the image a card
  // ships with is `StationeryOrderCompleted.backdropId`, joined on `stationeryDraftId`.
  | 'DigitalCardBackdropSelected'
  | 'FormFieldInteracted'
  | 'ImageFormatChecked'
  | 'ResolutionWarningShown'
  | 'SavedPromoCodeInvalidated'
  | 'ShippingAddressAutoPopulated'
  | 'SmartyApiSuccess';

/**
 * Raw `action` values emitted through the generic `trackError` action.
 *
 * `actionType: 'error'` makes `trackFunc` **append `Error` to the event name**, so these are not
 * the names that reach Segment: `'SmartyApi'` arrives as `SmartyApiError`, and `'loadFontError'`
 * arrives as `loadFontErrorError` (a pre-existing quirk of that event, preserved deliberately).
 * Same dashboard-contract caveat as {@link CardCustomizerTrackedAction} applies.
 */
export type CardCustomizerTrackedErrorAction = 'loadFontError' | 'MessagingStripeRedirectTimeout' | 'PhotoSelectionError' | 'SendPremiumDigitalCard' | 'SmartyApi';

/**
 * Every `name` emitted through the generic `buttonInteracted` action.
 *
 * All of these fire the shared `MessagingButtonInteracted` event, so `name` — not `action` — is
 * what identifies them in Amplitude. Same dashboard-contract caveat as
 * {@link CardCustomizerTrackedAction} applies; use `findReferences` on a member to find emitters.
 *
 * Note: this is deliberately tighter than the sibling `Card.telemetry.ts` `buttonInteracted`,
 * which takes a free-form payload.
 */
export type CardCustomizerButtonName =
  | 'digitalEnvelopeCustomizationCompleted'
  | 'exitCustomizationWarning_cancel'
  | 'exitCustomizationWarning_openCta'
  | 'exitCustomizationWarning_viewDesignsCta'
  | 'exitCustomizationWarning_viewDraftsCta'
  | 'exitCustomization_returnToDraftsCta'
  | 'promoCodeInput_applyCta'
  | 'promoCodeInput_removeCta'
  | 'previewAllPostcards'
  // A free (Basic Digital V2) draft tapped a design option only Premium Digital includes, which
  // applies the option, moves the draft onto the paid product and opens the upsell dialog.
  // `control` and `value` say which option did the selling. `upgradeConfirmed` is the dialog's
  // "Upgrade to Premium"; `undo` is its "Stay in Basic", which takes the upgrade back. All three
  // carry the same pair so they stay comparable -- together they are the in-editor upsell's
  // conversion rate. `undo` fires once the draft is back on Basic, and also carries `source`:
  // `button`, or `backdrop` for a click outside the dialog, which declines the same way -- and
  // `upgradeLanded`, false when the upgrade failed before the decline took it back.
  | 'premiumOptionUpsell_optionClicked'
  | 'premiumOptionUpsell_upgradeConfirmed'
  | 'premiumOptionUpsell_undoClicked'
  // The upgrade, or the "Stay in Basic" undo, did not land and the user was shown an error toast.
  // Same `control`/`value` pair as above, so a failed upgrade can be taken out of the conversion
  // rate and a failed undo explains a card left premium without a confirmation.
  | 'premiumOptionUpsell_upgradeFailed'
  | 'premiumOptionUpsell_undoFailed'
  // The "Leave Basic Digital?" confirm a free draft sees before switching format: `shown` carries
  // the `targetProductId` it was asked about, `stayClicked` the `source` (`button`, or `dismiss` for a backdrop click or Escape).
  // Named apart from the upsell's events because both dialogs offer "Stay in Basic". The confirmed
  // switch is `customizer_productChoiceGroupClicked` with `from: basicDigitalV2`.
  | 'leaveBasicDigitalConfirm_shown'
  | 'leaveBasicDigitalConfirm_stayClicked'
  // The "Start a new Basic Digital card?" confirm a paper or Premium Digital draft sees before the
  // switcher starts a new Basic Digital draft: `shown` carries the `targetProductId`,
  // `keepEditingClicked` the `source` (`button`, or `dismiss` for a backdrop click or Escape). The
  // confirmed start is `customizer_productChoiceGroupClicked` with `to: basicDigitalV2`.
  | 'startBasicDigitalConfirm_shown'
  | 'startBasicDigitalConfirm_keepEditingClicked'
  | 'stickyCta_nextButton_withValidation'
  // The free Basic Digital V2 finish action. A separate name because it skips billing-address
  // validation entirely, so it carries no `isValid` and is not comparable to the paid CTA.
  | 'stickyCta_nextButton_free'
  // Card checkout's Delivery section with Messaging+ offered (`printCardCheckoutMessagingPlus`).
  // `deliveryOptionSelected` carries the `option` picked: `basic`, `premium` or `messagingPlus`.
  // `messagingPlusAddedToCart` fires once Messaging+ lands in the cart beside the card;
  // `messagingPlusAddToCartFailed` carries a `reason`: `alreadyPurchased` or `error`.
  | 'digitalReview_deliveryOptionSelected'
  | 'digitalReview_messagingPlusAddedToCart'
  | 'digitalReview_messagingPlusAddToCartFailed'
  | 'StudioImage_applyToCard'
  | 'submitChangesCta';

export interface UnableToCheckoutErrorArgs extends Record<string, unknown> {
  action?: 'DigitalCardCheckout' | 'InternationalDigitalCardCheckout';
  name: string;
  error?: unknown;
}

const cardTelemetry = createTelemetryObject({
  pages: {
    customize: () => ({
      category: CATEGORY,
      pagePrefix: PAGE_PREFIX,
      page: PAGE,
    }),
    premiumDigitalEmailEditorDialog: () => ({
      category: CATEGORY,
      pagePrefix: PAGE_PREFIX,
      page: 'premiumDigitalEmailEditorDialog',
    }),
  },
  actions: {
    /**
     * Generic escape hatch for one-off events: supplies the `category`/`pagePrefix`/`page`
     * boilerplate so a distinct action needs no dedicated named method.
     *
     * Only for events with no `actionType` — that field renames the event on the wire
     * (`'SmartyApi'` + `'error'` → `SmartyApiError`), so those events keep a named method.
     *
     * Prefer a named method when an event has multiple call sites (the method enforces payload
     * consistency across callers) or when the payload needs a shared computation.
     *
     * On the deliberate loss of payload typing: the named methods this replaced typed each
     * payload (`AddressType`, `LayerType`, `'low' | 'high'`, …); `extraInfo` here is
     * `Record<string, unknown>`, so payload keys and value shapes are unchecked. That is an
     * accepted trade, not an oversight. Event *names* — the thing dashboards actually key on,
     * and the thing a typo silently breaks — are now union-typed and `findReferences`-able,
     * which they were not before. Payload drift, by contrast, is caught in the analytics
     * pipeline rather than at compile time, and these payloads are written once at a single
     * call site and rarely touched again. A mapped name-to-payload type could recover both,
     * but it buys little for one-off events and is not worth the indirection here; reach for a
     * named method instead on any event where payload consistency genuinely matters.
     */
    track: (action: CardCustomizerTrackedAction, extraInfo?: Record<string, unknown>) => ({
      category: CATEGORY,
      pagePrefix: PAGE_PREFIX,
      page: PAGE,
      action,
      extraInfo,
    }),
    /**
     * `track` for error events: adds `actionType: 'error'`, which logs at error level and
     * appends `Error` to the event name on the wire. See {@link CardCustomizerTrackedErrorAction}.
     */
    trackError: (action: CardCustomizerTrackedErrorAction, extraInfo?: Record<string, unknown>) => ({
      category: CATEGORY,
      pagePrefix: PAGE_PREFIX,
      page: PAGE,
      action,
      actionType: 'error',
      extraInfo,
    }),
    /**
     * `track` for the shared `MessagingButtonInteracted` event, where `name` carries the
     * identity instead of `action`. See {@link CardCustomizerButtonName}.
     *
     * TODO: overlaps with `messagingButtonInteracted`, which emits the same event with a
     * free-form `name: string`. The two are to be merged in the next phase — this one is the
     * intended survivor, since the union keeps `name` typo-proof and `findReferences`-able.
     * Merging is deferred only because `messagingButtonInteracted` spans 11 files, past the
     * agreed migration threshold. Prefer this method for new button events.
     */
    buttonInteracted: (name: CardCustomizerButtonName, extraInfo?: Record<string, unknown>) => ({
      category: CATEGORY,
      pagePrefix: PAGE_PREFIX,
      page: PAGE,
      action: 'MessagingButtonInteracted',
      // `name` is spread last so an `extraInfo.name` can never clobber the union-typed identity.
      extraInfo: { ...extraInfo, name },
    }),
    /**
     * Also emits `MessagingButtonInteracted`, but maps `property`/`value` onto `name`/`value`
     * rather than taking a `name` directly — so it is a third shape for the same event. Out of
     * scope for the `buttonInteracted` merge noted above (20 call-site files), but worth folding
     * in if that consolidation ever goes further.
     */
    customizationButtonInteracted: (args: CustomizationButtonInteractedArgs) => ({
      category: CATEGORY,
      pagePrefix: PAGE_PREFIX,
      page: PAGE,
      action: 'MessagingButtonInteracted',
      extraInfo: {
        name: args.property,
        value: args.value,
      },
    }),
    unableToCheckoutError: (args: UnableToCheckoutErrorArgs) => ({
      category: CATEGORY,
      pagePrefix: PAGE_PREFIX,
      page: PAGE,
      action: args.action || 'CardCheckout',
      actionType: 'error',
      extraInfo: { ...args },
    }),

    /**
     * TODO: superseded by `buttonInteracted`, which emits this same event with `name` constrained
     * to {@link CardCustomizerButtonName}. To be merged into it in the next phase; kept for now
     * because its 11 call-site files exceed the agreed migration threshold. Don't add new callers —
     * add the `name` to the union and use `buttonInteracted` instead.
     */
    messagingButtonInteracted: (name: string) => ({
      category: CATEGORY,
      pagePrefix: PAGE_PREFIX,
      page: PAGE,
      action: 'MessagingButtonInteracted',
      extraInfo: {
        name,
      },
    }),
  },
});

export type EnrichedCardCustomizerExtraInfo = {
  stationeryDraftId: string | null;
  stationeryDraftFormat: StationeryDraftFormat | null;
  // `stationeryDraftFormat` only separates print from digital, so it cannot tell the free
  // Basic Digital V2 apart from paid premium digital. Carried on every customizer event so
  // free and premium sessions are separable, matching what the PDP already sends as
  // `selectedFormat`.
  digitalProductType: DigitalProductType | null;
  stationeryTemplateCategory: StationeryTemplateCategoryEnum | null;
  themeId: string | null;
  step: NavigationStep | null;
  pageOverride: Maybe<'pdpModal'>;
  orderNumber: Maybe<string>;
  isComingFromStripeCheckout?: boolean;
};

const {
  TelemetryProvider: CardCustomizerTelemetryProvider,
  useTelemetry: useCardCustomizerTelemetry,
  enrichTelemetryExtraInfo: enrichCardCutomizerTelemetryExtraInfo,
} = createTelemetry(cardTelemetry, undefined, undefined, {
  stationeryDraftId: null,
  stationeryTemplateCategory: null,
  themeId: null,
  step: null,
  orderNumber: null,
  stationeryDraftFormat: null,
  digitalProductType: null,
} as EnrichedCardCustomizerExtraInfo);

export { CardCustomizerTelemetryProvider, useCardCustomizerTelemetry, enrichCardCutomizerTelemetryExtraInfo };
